// ═══════════════════════════════════════════════════════════════════
// SITE-WIDE SEARCH BAR
// Instant dropdown results from the search index
// ═══════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { Search, X, ArrowRight } from "lucide-react";
import { search as runSearch, SearchResult } from "@/lib/search";
import type { SearchItem } from "@/data/searchIndex";

const CATEGORY_COLORS: Record<SearchItem["category"], string> = {
  Treatment: "bg-[#c8956c]/10 text-[#c8956c]",
  Condition: "bg-[#4a7c59]/10 text-[#4a7c59]",
  Technology: "bg-[#5b7fa6]/10 text-[#5b7fa6]",
  Service: "bg-[#9b6b9b]/10 text-[#9b6b9b]",
  Info: "bg-[#888]/10 text-[#888]",
};

// Suggested quick-search terms shown when the bar is focused but empty
const SUGGESTIONS = [
  "melasma", "crows feet", "double chin", "acne scars",
  "dark circles", "rosacea", "hair loss", "botox",
];

interface SearchBarProps {
  onClose?: () => void;
  autoFocus?: boolean;
  className?: string;
}

export default function SearchBar({ onClose, autoFocus = false, className = "" }: SearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const doSearch = useCallback((q: string) => {
    if (!q.trim()) {
      setResults([]);
      setIsOpen(false);
      setShowSuggestions(true);
      return;
    }
    setShowSuggestions(false);
    const scored = runSearch(q, 8);
    setResults(scored);
    setIsOpen(true);
    setActiveIndex(-1);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    doSearch(val);
  };

  const handleFocus = () => {
    if (!query.trim()) {
      setShowSuggestions(true);
    } else if (results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    doSearch(suggestion);
    inputRef.current?.focus();
  };

  const handleSelect = (route: string) => {
    navigate(route);
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setShowSuggestions(false);
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        handleSelect(results[activeIndex].route);
      } else if (results.length > 0) {
        handleSelect(results[0].route);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setShowSuggestions(false);
      setQuery("");
      onClose?.();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clearQuery = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="flex items-center gap-2 bg-white/80 border border-[#d4b896]/40 rounded-full px-3 py-1.5 shadow-sm focus-within:border-[#c8956c] focus-within:shadow-md transition-all">
        <Search className="w-4 h-4 text-[#c8956c] shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder="Search any skin concern…"
          className="flex-1 bg-transparent text-sm text-[#2c2c2c] placeholder-[#aaa] outline-none min-w-0"
          aria-label="Search the site"
          aria-autocomplete="list"
          aria-expanded={isOpen}
        />
        {query && (
          <button onClick={clearQuery} className="text-[#aaa] hover:text-[#c8956c] transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Suggestions panel */}
      {showSuggestions && !query.trim() && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl border border-[#e8ddd4] z-[9999] px-4 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#aaa] mb-2">
            Popular searches
          </p>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s); }}
                className="text-xs px-2.5 py-1 rounded-full bg-[#faf3ee] text-[#c8956c] border border-[#e8d5c0] hover:bg-[#f5e8d8] transition-colors capitalize"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl border border-[#e8ddd4] z-[9999] overflow-hidden">
          <ul role="listbox">
            {results.map((item, idx) => (
              <li
                key={`${item.route}-${idx}`}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(item.route);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                  idx === activeIndex ? "bg-[#faf3ee]" : "hover:bg-[#faf3ee]"
                } ${idx > 0 ? "border-t border-[#f0e8e0]" : ""}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-[#2c2c2c] truncate">{item.title}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${CATEGORY_COLORS[item.category]}`}>
                      {item.category}
                    </span>
                  </div>
                  <p className="text-xs text-[#888] truncate">{item.description}</p>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-[#c8956c] shrink-0" />
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 bg-[#faf8f5] border-t border-[#f0e8e0]">
            <p className="text-[10px] text-[#aaa] text-center">
              {results.length} result{results.length !== 1 ? "s" : ""} — press Enter to open first
            </p>
          </div>
        </div>
      )}

      {/* No results */}
      {isOpen && query.trim() && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl border border-[#e8ddd4] z-[9999] px-4 py-4 text-center">
          <p className="text-sm text-[#888]">No results for "<strong className="text-[#2c2c2c]">{query}</strong>"</p>
          <p className="text-xs text-[#aaa] mt-1">Try "botox", "acne scars", or "dark circles"</p>
        </div>
      )}
    </div>
  );
}
